/**
 * Reservation Booking
 */
(() => {
  'use strict';

  if (window.ReservationBooking) return;

  const PAST_BUFFER_MIN = 10;
  const MINUTES_PER_HOUR = 60;
  const REFRESH_DEBOUNCE_MS = 150;

  /** @returns {Object|null} window.A9DateUtils once Day.js plugins are ready, else null. */
  function getDateUtils() {
    const utils = typeof window !== 'undefined' ? window.A9DateUtils : null;
    return utils && utils.hasTz && utils.hasTz() ? utils : null;
  }

  async function buildAuthHash({ customerId, customerEmail, salt }) {
    if (!customerId || !customerEmail || !salt) return null;
    const message = String(customerId) + customerEmail + salt;
    if (typeof window.sha256 === 'function') return window.sha256(message);
    if (window.crypto && window.crypto.subtle) {
      const bytes = new TextEncoder().encode(message);
      const digest = await window.crypto.subtle.digest('SHA-256', bytes);
      return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, '0')).join('');
    }
    return null;
  }

  function readSlotsMembershipMeta(slots) {
    const slotPricing = window.MembershipSlotPricing;
    return slotPricing && slotPricing.readSlotsMembershipMeta ? slotPricing.readSlotsMembershipMeta(slots) : null;
  }

  const AvailabilityRules = {
    filterTodayPast(slots, ymd, timeZone) {
      const utils = getDateUtils();
      if (!utils || !Array.isArray(slots)) return slots || [];
      if (ymd !== utils.currentYmd(timeZone)) return slots;

      const nowMin = utils.time12hToMinutes(utils.currentTime12h(timeZone));
      const cutoff = nowMin + PAST_BUFFER_MIN;
      const normalize = window.SlotApiService ? window.SlotApiService.normalizeZulu : (s) => s;

      return slots.filter((slot) => {
        if (!slot || !slot.from) return false;
        const local = dayjs.utc(normalize(slot.from)).tz(timeZone);
        if (!local.isValid()) return true;
        const mins = local.hour() * MINUTES_PER_HOUR + local.minute();
        return mins > cutoff;
      });
    },

    /** @returns {boolean} whether the grid still offers `fromZulu` as available. */
    isSlotAvailable(slots, fromZulu) {
      const api = window.SlotApiService;
      if (!api) return false;
      return !!api.findSlotRowByFrom(slots, fromZulu, { requireAvailable: true });
    }
  };

  class SlotGridStore {
    constructor(slotApi) {
      this.api = slotApi;
      this.inflight = new Map();
      this.latestKey = null;
    }

    static key(params) {
      return [params.facilityId, params.ymd, params.duration, params.suiteCount, params.clubsKey, params.identityKey || 'guest'].join('|');
    }

    async get(params) {
      const key = SlotGridStore.key(params);
      this.latestKey = key;

      if (!this.inflight.has(key)) {
        const request = this.api
          .getSlots(params.facilityId, {
            from: params.from,
            to: params.to,
            duration: params.duration,
            suiteCount: params.suiteCount,
            clubs: params.clubs,
            customerId: params.customerId,
            customerHash: params.customerHash
          })
          // `meta` rides the array non-enumerably, so it must be read before any filter or map.
          .then((slots) => ({ slots, membershipMeta: readSlotsMembershipMeta(slots) }))
          .finally(() => this.inflight.delete(key));
        this.inflight.set(key, request);
      }

      const { slots, membershipMeta } = await this.inflight.get(key);
      return { slots, key, membershipMeta };
    }

    isStale(key) {
      return key !== this.latestKey;
    }
  }

  class ReservationBookingController {
    /**
     * @param {Object} cfg
     * @param {HTMLElement} cfg.picker     <datetime-picker> element
     * @param {HTMLElement} cfg.summary    <reservation-summary variant="multi"> element
     * @param {Object} cfg.config          { facilityId, timeZone, apiBase, sectionId,
     *                                        membership, customerId, customerEmail, salt }
     */
    constructor({ picker, summary, config }) {
      this.picker = picker;
      this.summary = summary;
      this.config = config;
      this.slotApi = new window.SlotApiService(config.apiBase);
      this.store = new SlotGridStore(this.slotApi);

      this.state = {
        suiteCount: null,
        duration: null,
        selectedYmd: null,
        selectedSlot: null,
        slotConflict: false,
        loading: false,
        availableSlots: [],
        membershipMeta: null
      };

      this._refreshTimer = null;
      this._submitting = false;
      this.currentVariantId = null;
      this.cartMembershipHours = { byPeriod: {}, byDay: {} };
      /** Bumped on every cart change; the grid's stamp must match for a backend grant to count. */
      this.cartRevision = 0;
      this.gridCartRevision = null;
      this._slotIdentityPromise = null;
      this._slotIdentityRejected = false;

      this.onDatePicked = this.onDatePicked.bind(this);
      this.onTimePicked = this.onTimePicked.bind(this);
      this.onClubsChange = this.onClubsChange.bind(this);
      this.onReserve = this.onReserve.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
    }

    init() {
      this.readVariantContext();

      this.picker.addEventListener('datetime-picker:date', this.onDatePicked);
      this.picker.addEventListener('datetime-picker:change', this.onTimePicked);
      document.addEventListener('club-rental:change', this.onClubsChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (event.data && event.data.sectionId !== this.config.sectionId) return;
          if (event.data && event.data.variant) this.currentVariantId = event.data.variant.id;
          this.onVariantChange();
        });
        this.cartUpdateUnsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }

      this.summary.bookingController = this;
      this.pushSummaryState();
      this.refreshMembershipHoursUsedInCart();
    }

    async refreshMembershipHoursUsedInCart() {
      if (!window.MembershipSlotPricing) return;
      this.cartMembershipHours = await window.MembershipSlotPricing.getMembershipHoursUsedInCart(this.config.timeZone);
      if (typeof this.summary.recompute === 'function') this.summary.recompute();
    }

    readVariantContext() {
      this.state.suiteCount = this.readOptionInt(['suites', 'suite']) || null;
      this.state.duration = this.readOptionFloat(['hours', 'hour']) || null;
    }

    readOptionValue(slugs) {
      for (const slug of slugs) {
        const input = document.querySelector(`[data-option-slug="${slug}"] input:checked`);
        if (input) return input.value;
        const select = document.querySelector(`[data-option-slug="${slug}"] select`);
        if (select) return select.value;
      }
      return null;
    }

    readOptionInt(slugs) {
      const value = parseInt(this.readOptionValue(slugs), 10);
      return Number.isFinite(value) ? value : null;
    }

    readOptionFloat(slugs) {
      const value = parseFloat(this.readOptionValue(slugs));
      return Number.isFinite(value) ? value : null;
    }

    onVariantChange() {
      const previousSuite = this.state.suiteCount;
      const previousDuration = this.state.duration;
      this.readVariantContext();

      if (this.state.suiteCount !== previousSuite || this.state.duration !== previousDuration) {
        this.scheduleRefresh();
      }
    }

    onDatePicked(event) {
      this.state.selectedYmd = event.detail.ymd;
      this.state.selectedSlot = null;
      this.state.slotConflict = false;

      this.autoSelectDayType(event.detail.ymd);
      this.scheduleRefresh({ immediate: true });
      this.pushSummaryState();
    }

    onTimePicked(event) {
      this.state.selectedSlot = {
        from: event.detail.from,
        to: event.detail.to,
        label: event.detail.time
      };
      this.state.slotConflict = false;
      this.pushSummaryState();
    }

    autoSelectDayType(ymd) {
      const utils = getDateUtils();
      const wrapper = document.querySelector('[data-option-slug="day-of-week"]');
      if (!utils || !wrapper) return;

      const mdy = utils.ymdToMdy ? utils.ymdToMdy(ymd, this.config.timeZone) : dayjs(ymd, 'YYYY-MM-DD').format('M/D/YYYY');
      const dayType = utils.dayTypeFromMdy(mdy, this.config.timeZone);
      const target = dayType === 'weekend' ? 'weekend' : 'weekday';

      const input = Array.from(wrapper.querySelectorAll('input')).find(
        (radio) => (radio.value || '').toLowerCase().trim() === target
      );
      if (input && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    onClubsChange() {
      this.scheduleRefresh();
    }

    readClubs() {
      const clubs = { left: { adult: 0, kid: 0 }, right: { adult: 0, kid: 0 } };
      document.querySelectorAll('[data-club-rental-item]').forEach((item) => {
        const qtyInput = item.querySelector('[data-club-rental-qty]');
        const qty = parseInt(qtyInput && qtyInput.value, 10) || 0;
        if (qty <= 0) return;

        const label = (item.dataset.clubRentalTypeLabel || '').toLowerCase();
        const ageKey = label.includes('kid') || label.includes('junior') ? 'kid' : 'adult';

        item.querySelectorAll('[data-club-rental-hand][aria-pressed="true"]').forEach((hand) => {
          const side = hand.dataset.hand;
          if (side === 'left' || side === 'right') clubs[side][ageKey] += qty;
        });
      });

      return window.SlotApiService.hasClubs(clubs) ? clubs : null;
    }

    clubsKey(clubs) {
      if (!clubs) return 'noclubs';
      return `${clubs.left.adult}-${clubs.left.kid}-${clubs.right.adult}-${clubs.right.kid}`;
    }

    collectClubRentalItems(hold, dateLabel, timeRangeLabel, timeStamp) {
      const items = [];
      document.querySelectorAll('[data-club-rental-item]').forEach((item) => {
        const qtyInput = item.querySelector('[data-club-rental-qty]');
        const quantity = parseInt(qtyInput && qtyInput.value, 10) || 0;
        if (quantity <= 0) return;

        item.querySelectorAll('[data-club-rental-hand][aria-pressed="true"]').forEach((hand) => {
          const variantId = parseInt(hand.dataset.variantId, 10);
          if (!variantId) return;
          items.push({
            id: variantId,
            quantity,
            properties: {
              Date: dateLabel,
              Time: timeRangeLabel,
              _key: hold.key,
              _time_from: this.state.selectedSlot.from,
              _facility_id: Number(this.config.facilityId),
              _suite_combination_set_id: hold.suite_combination_set_id,
              _club_rental_timestamp: timeStamp
            }
          });
        });
      });
      return items;
    }

    scheduleRefresh(opts = {}) {
      if (opts.immediate) {
        clearTimeout(this._refreshTimer);
        this.refreshAvailability();
        return;
      }
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this.refreshAvailability(), REFRESH_DEBOUNCE_MS);
    }

    /** Customer identity for GET /slots, so per-slot membership hours come from the backend. */
    async _slotIdentity() {
      if (this._slotIdentityRejected) return null;
      if (!this.config.customerId) return null;
      if (!this._slotIdentityPromise) {
        this._slotIdentityPromise = buildAuthHash(this.config)
          .then((hash) => (hash ? { customerId: this.config.customerId, customerHash: hash } : null))
          .catch(() => null);
      }
      return this._slotIdentityPromise;
    }

    /** Hours the backend will cover for the selected slot; null when it was not asked. */
    backendConsumableHoursForSelection() {
      const api = window.SlotApiService;
      const slot = this.state.selectedSlot;
      if (!api || !slot || !this.state.membershipMeta) return null;

      const row = api.findSlotRowByFrom(this.state.availableSlots, slot.from);
      return row ? api.membershipConsumableHours(row) : null;
    }

    /**
     * Hour credit the backend will cover for the selected slot (all suites); null when it was not
     * asked. Only a fresh grid answers, so the preview and the hold pool read the same figure.
     */
    creditHoursForSelection() {
      const api = window.SlotApiService;
      const credit = window.A9HourCredit;
      const slot = this.state.selectedSlot;
      if (!api || !credit || !slot || !this.state.membershipMeta || !this.isGridFresh()) return null;

      const row = api.findSlotRowByFrom(this.state.availableSlots, slot.from);
      return row ? credit.slotHours(row) : null;
    }

    /**
     * Credit for this booking when the membership paid nothing. Before a time is picked the
     * metafield only says whether credit applies; the hours come from a fresh grid alone.
     */
    resolveCredit(inputs, basePriceCents) {
      const credit = window.A9HourCredit;
      const dateUtils = getDateUtils();
      if (!credit || !dateUtils || basePriceCents <= 0) return null;

      if (!inputs.hasSelectedTime) {
        // No date yet: any grant still valid here counts, as on the standard PDP.
        const coverage = inputs.isTodayFallback
          ? credit.coverageForFacility(this.config.hourCredits, inputs.facilityId)
          : credit.coverageForDate(this.config.hourCredits, inputs.facilityId, dateUtils.mdyToYmd(inputs.dateMdy, inputs.tz), inputs.tz);
        return coverage.applies ? { isEstimate: true, weekdaysOnly: coverage.weekdaysOnly, hours: 0, offCents: 0 } : null;
      }

      // The grid figure already counts every suite — never multiply it again.
      const totalHours = inputs.duration * inputs.suiteCount;
      const { usedHours } = credit.lineDiscount(this.creditHoursForSelection(), totalHours);
      if (!(usedHours > 0)) return null;

      const priceCents = credit.discountedPriceCents(basePriceCents, usedHours, totalHours);
      return {
        isEstimate: false,
        hours: usedHours,
        paidHours: window.MembershipHoursUtils.roundHours(Math.max(0, totalHours - usedHours)),
        offCents: basePriceCents - priceCents,
        priceCents
      };
    }

    async refreshAvailability() {
      const utils = getDateUtils();
      const { selectedYmd, suiteCount, duration } = this.state;
      if (!utils || !selectedYmd || !suiteCount || !duration) return;

      const tz = this.config.timeZone;
      let bounds;
      try {
        bounds = window.SlotApiService.dayBoundsZulu(selectedYmd, tz);
      } catch (error) {
        return;
      }

      const clubs = this.readClubs();
      this.state.loading = true;
      this.picker.setLoading(true);
      this.pushSummaryState();

      const identity = await this._slotIdentity();

      let result;
      try {
        result = await this.store.get({
          facilityId: this.config.facilityId,
          ymd: selectedYmd,
          from: bounds.from,
          to: bounds.to,
          duration,
          suiteCount,
          clubs,
          clubsKey: this.clubsKey(clubs),
          customerId: identity ? identity.customerId : null,
          customerHash: identity ? identity.customerHash : null,
          identityKey: identity ? String(identity.customerId) : 'guest'
        });
      } catch (error) {
        this.state.loading = false;
        this.picker.setLoading(false);
        this.picker.setAvailableSlots([]);
        this.pushSummaryState();
        return;
      }

      if (this.store.isStale(result.key)) return;

      // Identity sent but unanswered: a bad salt or an older backend. Stop paying for the hash.
      if (identity && !result.membershipMeta) {
        this._slotIdentityRejected = true;
        this._slotIdentityPromise = null;
      }

      this.state.membershipMeta = result.membershipMeta;
      this.gridCartRevision = this.cartRevision;
      this.publishGridMembershipPreference();
      this.state.loading = false;
      const daySlots = AvailabilityRules.filterTodayPast(result.slots, selectedYmd, tz);
      this.state.availableSlots = daySlots;
      this.picker.setAvailableSlots(daySlots);

      this.revalidateSelectedSlot(daySlots);
      this.pushSummaryState();
    }

    revalidateSelectedSlot(slots) {
      if (!this.state.selectedSlot) return;
      const stillAvailable = AvailabilityRules.isSlotAvailable(slots, this.state.selectedSlot.from);
      this.state.slotConflict = !stillAvailable;
      this.picker.flagSelectionConflict(!stillAvailable);
    }

    pushSummaryState() {
      if (typeof this.summary.setBookingState !== 'function') return;
      const slot = this.state.selectedSlot;
      const date = slot && this.state.selectedYmd
        ? dayjs(this.state.selectedYmd, 'YYYY-MM-DD').format('ddd, MMMM D')
        : null;
      this.summary.setBookingState({
        hasSelection: !!slot,
        slotConflict: this.state.slotConflict,
        loading: this.state.loading,
        date,
        time: slot ? slot.label : null
      });

      if (typeof this.summary.recompute === 'function') this.summary.recompute();
    }

    canReserve() {
      return !!this.state.selectedSlot && !this.state.slotConflict && !this.state.loading;
    }

    /** Everything the pricing calculation needs from the page, or null when it isn't priceable yet. */
    readPricingInputs() {
      const dateUtils = getDateUtils();
      const { selectedYmd, selectedSlot } = this.state;
      const duration = this.readOptionFloat(['hours', 'hour']);
      const suiteCount = this.readOptionInt(['suites', 'suite']);
      if (!window.MembershipHoursUtils || !window.MembershipSlotPricing || !dateUtils || !duration || !suiteCount) return null;

      const variant = this.getSelectedVariantData();
      const standardPriceCents = variant ? Number(variant.price) || 0 : 0;
      if (!standardPriceCents) return null;

      const tz = this.config.timeZone;
      const hasSelectedDate = !!selectedYmd;

      return {
        tz,
        facilityId: Number(this.config.facilityId),
        membershipRaw: this.config.membership,
        duration,
        suiteCount,
        standardPriceCents,
        membershipMeta: this.state.membershipMeta,
        backendConsumableHours: this.backendConsumableHoursForSelection(),
        hasSelectedTime: !!(selectedYmd && selectedSlot),
        isTodayFallback: !hasSelectedDate,
        dateMdy: hasSelectedDate ? dateUtils.ymdToMdy(selectedYmd, tz) : dateUtils.currentMdy(tz),
        selectedSlot
      };
    }

    /**
     * Exact per-slot discount once a time is picked, otherwise an hours-only estimate for the date.
     * `backendGrant` comes from the caller so the price is decided from one reading of the grid answer.
     */
    resolveDiscount(inputs, basePriceCents, eligibility, backendGrant) {
      const membershipUtils = window.MembershipHoursUtils;
      const { membershipRaw, facilityId, dateMdy, duration, suiteCount, tz, selectedSlot } = inputs;
      if (basePriceCents <= 0) return null;

      const slotPricing = window.MembershipSlotPricing;
      const hasRows = !!membershipRaw && membershipUtils.hasMembershipSlotData(membershipRaw, facilityId);
      if (!hasRows && !backendGrant) return null;

      if (inputs.hasSelectedTime) {
        if (eligibility.weekendNotFree) return null;
        return membershipUtils.calculateMembershipDiscountForSlot({
          membershipData: membershipRaw,
          facilityId,
          dateStr: dateMdy,
          timeStr: selectedSlot.label,
          durationHours: duration,
          fullPriceCents: basePriceCents,
          hoursUsedByPeriod: this.cartMembershipHours.byPeriod,
          hoursUsedByDay: this.cartMembershipHours.byDay,
          facilityTimeZone: tz,
          suiteCount,
          consumableHoursCeiling: inputs.backendConsumableHours,
          preferredType: this.preferredMembershipType(),
          backendGrant
        });
      }

      const hoursInfo = slotPricing.getMembershipHoursForDate(membershipRaw, facilityId, dateMdy, duration, tz, suiteCount, this.preferredMembershipType());
      if (!hoursInfo) return null;

      const totalMembershipHoursNeeded = duration * suiteCount;
      const hourlyRate = basePriceCents / totalMembershipHoursNeeded;
      return {
        canUseMembership: true,
        isUnlimited: hoursInfo.isUnlimited,
        membershipHoursUsed: hoursInfo.usedHours,
        paidHours: Math.max(0, totalMembershipHoursNeeded - hoursInfo.usedHours),
        discountedPriceCents: Math.round(basePriceCents - hourlyRate * hoursInfo.usedHours),
        membershipHoursUsedByPeriod: hoursInfo.period ? [{ period: hoursInfo.period, usedHours: hoursInfo.usedHours }] : []
      };
    }

    getSlotPricingContext() {
      const emptyContext = { hasContext: false, isEstimate: false, isTodayFallback: false, basePriceCents: 0, finalPriceCents: 0, discountResult: null, creditResult: null, coveringPeriod: null, promoResult: null, tierResult: null, hoursSource: 'estimate' };

      const inputs = this.readPricingInputs();
      if (!inputs) return emptyContext;

      const slotPricing = window.MembershipSlotPricing;
      const eligibility = inputs.membershipRaw
        ? slotPricing.getMembershipEligibilityForSlot(inputs.membershipRaw, inputs.facilityId, inputs.dateMdy, inputs.tz)
        : { weekendNotFree: false };

      // A granted date has the plan's day rules already applied by the backend, so no weekend block applies on top.
      const backendGrant = inputs.hasSelectedTime
        ? slotPricing.buildBackendGrant(inputs.membershipMeta, inputs.backendConsumableHours, this.isGridFresh())
        : null;
      if (backendGrant) eligibility.weekendNotFree = false;

      const basePriceCents = inputs.standardPriceCents;
      const discountResult = this.resolveDiscount(inputs, basePriceCents, eligibility, backendGrant);

      // The covering period travels with the context so the summary can name the plan even when no
      // hours are granted (daily cap spent, balance empty). Reuse the one the discount already found.
      const grantedPeriod = discountResult && discountResult.membershipHoursUsedByPeriod[0]
        ? discountResult.membershipHoursUsedByPeriod[0].period
        : null;

      const finalPriceCents = discountResult && discountResult.canUseMembership
        ? discountResult.discountedPriceCents
        : basePriceCents;

      // Same roundHours as the cart property, so this and `_membership_hours.discount` agree on
      // whether the membership reduction actually reaches the Shopify cart line.
      const membershipDiscountsCartLine = !!(discountResult && discountResult.canUseMembership &&
        window.MembershipHoursUtils.roundHours(discountResult.membershipHoursUsed || 0) > 0);

      // The base the cart line will actually carry. Tier then promo land on it in that order,
      // the same arithmetic the checkout function runs — sizing the tier off anything else
      // makes the promo cap disagree with what checkout allows.
      // Membership pays first; credit only where it paid nothing (A9OS never splits a booking).
      const creditResult = membershipDiscountsCartLine ? null : this.resolveCredit(inputs, basePriceCents);
      const creditDiscountsCartLine = !!(creditResult && creditResult.offCents > 0);
      const hoursDiscountLine = membershipDiscountsCartLine || creditDiscountsCartLine;
      const lineBaseCents = membershipDiscountsCartLine
        ? finalPriceCents
        : (creditDiscountsCartLine ? creditResult.priceCents : basePriceCents);

      // Only once a real date exists — eligibility is per facility and period.
      const tierResult = inputs.isTodayFallback
        ? null
        : slotPricing.resolveTierDiscount({
            priceCents: lineBaseCents,
            membershipData: inputs.membershipRaw,
            facilityId: inputs.facilityId,
            dateStr: inputs.dateMdy,
            facilityTimeZone: inputs.tz,
            preferredType: this.preferredMembershipType()
          });
      const tierOffCents = tierResult && tierResult.offCents > 0 ? tierResult.offCents : 0;

      // Promo prices what is left of that same base; finalPriceCents stays membership-only — the summary subtracts the promo and the tier as their own rows.
      const promoBaseCents = Math.max(0, lineBaseCents - tierOffCents);
      const promoResult = inputs.isTodayFallback
        ? null
        : this.resolveBookingPromo(promoBaseCents);
      if (promoResult) promoResult.pricedAfterMembership = hoursDiscountLine;

      return {
        hasContext: true,
        isEstimate: !inputs.hasSelectedTime,
        isTodayFallback: inputs.isTodayFallback,
        // 'backend' means the hours are what will actually be charged, not an estimate.
        hoursSource: inputs.backendConsumableHours !== null && inputs.hasSelectedTime ? 'backend' : 'estimate',
        basePriceCents,
        // The price after hours — membership's, or credit's when the membership paid nothing.
        finalPriceCents: creditDiscountsCartLine ? creditResult.priceCents : finalPriceCents,
        discountResult,
        creditResult,
        coveringPeriod: grantedPeriod || slotPricing.getCoveringPeriod(inputs.membershipRaw, inputs.facilityId, inputs.dateMdy, inputs.tz, this.preferredMembershipType()),
        promoResult,
        tierResult
      };
    }

    resolveBookingPromo(subtotalCents) {
      if (!window.A9PromoUtils) return null;
      const { selectedYmd, selectedSlot } = this.state;

      return window.A9PromoUtils.resolveForBooking({
        promosRaw: this.config.promos,
        facilityId: Number(this.config.facilityId),
        dateYmd: selectedYmd,
        timeZone: this.config.timeZone,
        slotFromZulu: selectedSlot ? selectedSlot.from : null,
        subtotalCents
      });
    }

    onReserve() {
      if (!this.canReserve()) return;

      const utils = getDateUtils();
      const startLabel = this.state.selectedSlot.label;
      const date = utils
        ? utils.formatMdyShort(this.state.selectedYmd)
        : dayjs(this.state.selectedYmd, 'YYYY-MM-DD').format('MMMM D, YYYY');
      const time = utils ? utils.formatTimeRange12h(startLabel, this.state.duration) : startLabel;
      const location = window.LocationConfirmationPopup
        ? window.LocationConfirmationPopup.getProductLocationFromPage()
        : null;

      const onConfirm = () => this.createHoldAndAddToCart();

      if (window.LocationConfirmationPopup && typeof window.LocationConfirmationPopup.show === 'function') {
        window.LocationConfirmationPopup.show(
          {
            type: 'reservation',
            location: location ? location.location_display || location.city_state : '',
            city_state: location ? location.city_state : null,
            facility: location ? location.facility : null,
            date,
            time
          },
          onConfirm
        );
      } else {
        onConfirm();
      }
    }

    async createHoldAndAddToCart() {
      if (this._submitting) return;
      this._submitting = true;

      const reserveButton = this.summary.reserveButton;
      if (reserveButton) {
        reserveButton.classList.add('_loading');
        reserveButton.disabled = true;
        reserveButton.setAttribute('aria-busy', 'true');
      }

      try {
        const { selectedSlot, suiteCount, duration } = this.state;
        const tz = this.config.timeZone;

        if (!selectedSlot || !AvailabilityRules.isSlotAvailable(this.state.availableSlots, selectedSlot.from)) {
          this.state.slotConflict = true;
          this.picker.flagSelectionConflict(true);
          this.pushSummaryState();
          throw new Error('This time slot is no longer available. Please choose another time.');
        }

        const holdBody = {
          from: selectedSlot.from,
          to: selectedSlot.to,
          suite_count: suiteCount
        };
        const clubs = this.readClubs();
        if (clubs) holdBody.clubs = clubs;

        // Exclusive flags, no fallback between them: the slot's own figures pick the pool.
        const holdPool = window.A9HourCredit
          ? window.A9HourCredit.pickHoldPool({
              membershipHours: this.backendConsumableHoursForSelection(),
              creditHours: this.creditHoursForSelection(),
              membershipKnown: this.hasActiveMembership(),
              creditAllowed: true
            })
          : (this.hasActiveMembership() ? 'membership' : null);

        if (holdPool) {
          const hash = await buildAuthHash(this.config);
          if (hash) {
            holdBody[holdPool] = true;
            holdBody.customer_id = this.config.customerId;
            holdBody.customer_hash = hash;
          }
        }

        const hold = await this.slotApi.createHold(this.config.facilityId, holdBody);
        const holdMembership = this.resolveHoldVerdict(hold, !!holdBody.membership);
        const holdCredit = holdBody.hour_credit ? window.A9HourCredit.readHold(hold) : null;
        const cartPayload = this.buildCartPayload(hold, duration, tz, holdMembership, holdCredit);
        await this.addToCart(cartPayload);
        this.afterReserveSuccess();
      } catch (error) {
        this.summary.showError(error && error.message ? error.message : 'Unable to reserve this slot');
        this.scheduleRefresh({ immediate: true });
      } finally {
        this._submitting = false;
        if (reserveButton) {
          reserveButton.classList.remove('_loading');
          reserveButton.removeAttribute('aria-busy');
        }
        if (typeof this.summary.updateReserveState === 'function') this.summary.updateReserveState();
      }
    }

    resetForm() {
      this.store.latestKey = null;

      this.state.selectedYmd = null;
      this.state.selectedSlot = null;
      this.state.slotConflict = false;
      this.state.loading = false;
      this.state.availableSlots = [];
      // Per-slot membership hours were computed against a balance that has just changed.
      this.state.membershipMeta = null;
      this.cartRevision += 1;
      this.gridCartRevision = null;

      if (typeof this.picker.reset === 'function') this.picker.reset();
      this.resetClubRentals();
      if (typeof this.summary.resetConsent === 'function') this.summary.resetConsent();

      this.pushSummaryState();
    }

    afterReserveSuccess() {
      this.resetForm();
      this.refreshMembershipHoursUsedInCart();
      if (typeof this.summary.showSuccess === 'function') this.summary.showSuccess();
    }

    onCartUpdate() {
      if (this._submitting) return;
      this.resetForm();
      this.refreshMembershipHoursUsedInCart();
    }

    resetClubRentals() {
      document.querySelectorAll('product-club-rental').forEach((node) => {
        if (typeof node.reset === 'function') node.reset();
      });
    }

    /** The hold's membership verdict plus whether we asked for it — the cart property branches on both. */
    resolveHoldVerdict(hold, requested) {
      const slotPricing = window.MembershipSlotPricing;
      const verdict = slotPricing
        ? slotPricing.readHoldMembership(hold)
        : { applied: hold && hold.membership != null, consumableHours: null, rejectionReason: null };
      return Object.assign({ requested: requested }, verdict);
    }

    buildCartPayload(hold, duration, tz, holdMembership, holdCredit) {
      const { selectedYmd, selectedSlot } = this.state;
      const variantId = this.getSelectedVariantId();
      const timeStamp = Date.now();
      const timeRangeLabel = this.buildTimeRangeLabel(selectedSlot, tz);
      const dateLabel = dayjs(selectedYmd, 'YYYY-MM-DD').format('MMMM D, YYYY');
      const membershipRaw = this.config.membership || null;

      const properties = {
        Date: dateLabel,
        Time: timeRangeLabel,
        _time_from: selectedSlot.from,
        _key: hold.key,
        _suite_combination_set_id: hold.suite_combination_set_id,
        _facility_id: Number(this.config.facilityId),
        _booking_start_timestamp: timeStamp
      };

      // Always written, even for a non-member: the promo is nested here, so a missing property
      // would drop it. Mirrors the zero-valued fallback the popup writes.
      // Credit and membership never share a booking: on a credit hold the membership carrier
      // stays empty and only holds the promo and the tier entitlement.
      const membershipHours = (!holdCredit && this.buildMembershipHoursProperty(tz, holdMembership)) || {
        status: 'success',
        useWeekendPrice: false,
        discount: 0,
        usedHours: 0,
        period: null,
        periodsUsed: []
      };
      // One reading of the tier entitlement for the whole line, whichever carrier was chosen —
      // the builders must not each answer it from their own argument set.
      membershipHours.tierEligible = this.tierEligibleForBooking(tz);
      properties._membership_hours = membershipHours;

      if (holdCredit && holdCredit.applied) {
        // The hold's figure is what A9OS reserved, already for every suite.
        const creditHours = holdCredit.hours !== null ? holdCredit.hours : this.creditHoursForSelection();
        window.A9HourCredit.applyToLine(properties, creditHours, duration * this.state.suiteCount);
      } else if (holdCredit) {
        // Still held at 201 — the booking is billed at the day's rate.
        console.warn('ReservationBooking: hour credit not applied to the hold', holdCredit.rejectionReason);
      }

      // Reservation line only — club rentals share the date but are excluded.
      if (window.A9PromoUtils) {
        window.A9PromoUtils.attachPromoToLine(
          properties,
          membershipHours,
          this.getSlotPricingContext().promoResult
        );
      }

      const payload = {
        items: [{ id: variantId, quantity: 1, properties }],
        attributes: {
          [`#${hold.key} From`]: selectedSlot.from,
          [`#${hold.key} To`]: selectedSlot.to,
          [`#${hold.key} Suite Combination Set`]: hold.suite_combination_set_id,
          [`#${hold.key} Item Key`]: hold.key,
          _customer_membership: membershipRaw ? JSON.stringify(membershipRaw) : ''
        }
      };

      this.collectClubRentalItems(hold, dateLabel, timeRangeLabel, timeStamp)
        .forEach((clubItem) => payload.items.push(clubItem));

      const miniCart = document.querySelector('mini-cart');
      if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
        payload.sections = miniCart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
      }

      return payload;
    }

    /**
     * Whether the tier percent may be charged on the picked date at this facility. The SupaEasy
     * function cannot read the customer metafield, so the theme answers it here.
     */
    tierEligibleForBooking(tz) {
      const slotPricing = window.MembershipSlotPricing;
      const dateUtils = getDateUtils();
      const { selectedYmd } = this.state;
      if (!slotPricing || !slotPricing.resolveTierEligibility || !dateUtils || !selectedYmd) return false;

      return slotPricing.resolveTierEligibility({
        membershipData: this.config.membership,
        facilityId: Number(this.config.facilityId),
        dateStr: dateUtils.ymdToMdy(selectedYmd, tz),
        facilityTimeZone: tz,
        preferredType: this.preferredMembershipType()
      });
    }

    buildMembershipHoursProperty(tz, holdMembership) {
      const slotPricing = window.MembershipSlotPricing;
      const dateUtils = getDateUtils();
      const { selectedYmd, selectedSlot, duration, suiteCount } = this.state;
      if (!slotPricing || !dateUtils || !selectedSlot) return null;

      const dateMdy = dateUtils.ymdToMdy(selectedYmd, tz);

      // The hold succeeded but the membership did not apply — the booking is billed at the day's rate.
      if (holdMembership && holdMembership.requested && !holdMembership.applied) {
        return {
          status: 'error',
          message: holdMembership.rejectionReason || 'Customer membership is not valid',
          useWeekendPrice: false
        };
      }

      return slotPricing.buildMembershipHoursCartProperty({
        membershipData: this.config.membership,
        facilityId: Number(this.config.facilityId),
        dateStr: dateMdy,
        timeStr: selectedSlot.label,
        durationHours: duration,
        hoursUsedInCartByPeriod: this.cartMembershipHours.byPeriod,
        hoursUsedInCartByDay: this.cartMembershipHours.byDay,
        facilityTimeZone: tz,
        suiteCount: suiteCount,
        consumableHours: holdMembership ? holdMembership.consumableHours : null,
        gridConsumableHours: this.backendConsumableHoursForSelection(),
        preferredType: this.preferredMembershipType(),
        membershipMeta: this.state.membershipMeta,
        gridFresh: this.isGridFresh()
      });
    }

    /** The loaded grid still answers for the current cart — the grant's precondition. */
    isGridFresh() {
      return this.gridCartRevision !== null && this.gridCartRevision === this.cartRevision;
    }

    preferredMembershipType() {
      const slotPricing = window.MembershipSlotPricing;
      return slotPricing?.resolvePreferredType?.(Number(this.config.facilityId), this.state.membershipMeta) || null;
    }

    publishGridMembershipPreference() {
      const planKey = this.state.membershipMeta?.planKey;
      if (!planKey) return;
      window.MembershipSlotPricing?.publishMembershipPreference?.(Number(this.config.facilityId), planKey, 'grid');
    }

    hasActiveMembership() {
      if (this.state.membershipMeta?.active === true) return true;
      const utils = window.MembershipHoursUtils;
      const membershipRaw = this.config.membership;
      const facilityId = Number(this.config.facilityId);
      if (!utils || !membershipRaw || !facilityId) return false;
      return !!(utils.hasMembershipSlotData && utils.hasMembershipSlotData(membershipRaw, facilityId));
    }

    getSelectedVariantId() {
      const radios = document.querySelector(`variant-radios[data-section="${this.config.sectionId}"]`)
        || document.querySelector('variant-radios')
        || document.querySelector('variant-selects');
      if (radios && radios.currentVariant && radios.currentVariant.id) return Number(radios.currentVariant.id);
      if (this.currentVariantId) return Number(this.currentVariantId);
      const input = document.querySelector(`#product-form-${this.config.sectionId} input[name="id"]`);
      return input ? Number(input.value) : null;
    }

    getSelectedVariantData() {
      const variantId = this.getSelectedVariantId();
      if (!variantId || !Array.isArray(this.config.variants)) return null;
      return this.config.variants.find((variant) => Number(variant.id) === variantId) || null;
    }

    buildTimeRangeLabel(slot, tz) {
      const start = slot.label;
      const end = dayjs.utc(window.SlotApiService.normalizeZulu(slot.to)).tz(tz);
      return end.isValid() ? `${start} - ${end.format('h:mm A')}` : start;
    }

    async addToCart(payload) {
      const response = await fetch(`${window.Shopify ? Shopify.routes.root : '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok || (data && data.status === 'error')) {
        throw new Error((data && (data.description || data.message)) || 'Unable to add to cart');
      }

      document.dispatchEvent(new CustomEvent('cart:updated'));
      const miniCart = document.querySelector('mini-cart');
      if (miniCart && typeof miniCart.renderContents === 'function' && payload.sections) {
        miniCart.renderContents(data);
      }
      return data;
    }

    destroy() {
      clearTimeout(this._refreshTimer);
      this.picker.removeEventListener('datetime-picker:date', this.onDatePicked);
      this.picker.removeEventListener('datetime-picker:change', this.onTimePicked);
      document.removeEventListener('club-rental:change', this.onClubsChange);
      if (this.variantUnsubscribe) this.variantUnsubscribe();
      if (this.cartUpdateUnsubscribe) this.cartUpdateUnsubscribe();
    }
  }

  function readConfig() {
    const node = document.getElementById('reservation-booking-config');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  }

  function boot() {
    const config = readConfig();
    const picker = document.querySelector('datetime-picker');
    const summary = document.querySelector('reservation-summary[data-variant="multi"]');
    if (!config || !picker || !summary || !window.SlotApiService) return;

    const controller = new ReservationBookingController({ picker, summary, config });
    controller.init();
    window.ReservationBooking = controller;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
